import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import { useDashboardDate, useTeacherTraining } from "../../../libs/data-access"; // adjust depth to match your file's actual location
import { calculateDefaultSession, showToast } from "../../../utils/utility";
import { ITeacherTrainingReportRow } from "../teacherTrainingReportRow.types";
import { generateExcelReport } from "../utils/generateTeacherAttendanceReport.util";
import useMasterData from "../../Hooks/useMasterData"; // reuse the same district/block source as the main Dashboard
import { AppContext } from "../../../AppContext";
import { ROLES } from "../../../Routes/routes";

/**
 * TODO: confirm actual endpoint names with backend — these are placeholders
 * following the same naming style as your other Nipun endpoints
 * (e.g. `NIPUN_Haryana_at_a_glancePr`, `ClassRoomObservation/...`).
 */
const ENDPOINTS = {
  OVERALL_STATS: "TeacherTrainingAttendance/GetOverallStats",
  SESSION_DATA: "TeacherTrainingAttendance/GetSessionWiseAttendance", // called once per session
  DOWNLOAD_MAP: "TeacherTrainingAttendance/DownloadMap",
  REPORT_ROWS: "TeacherTrainingAttendance/GetReportRows",
  SESSION_OPTIONS: "GetTrainingScheduleBySession", // called once per session
};

export default function useTeacherTrainingAttendance() {
  const { user, role } = useContext(AppContext);
   const {
     getDistrictByStateId,
     districts,
     getBlocksByDistrictId,
     blocks,
     getSession,
     sessions,
   } = useMasterData();

   const {
      fetchTeacherTrainingData,
      PostTeacherTrainingData,
      PutTeacherTrainingData,
      DeleteTeacherTrainingData
    } = useTeacherTraining()

  // ---- Filters (Apply Filter is explicit here, unlike the main Dashboard's auto-search) ----
  const [trainingTitle, setTrainingTitle] = useState<any>(
    ""
  );
   const [trainingTitleOptions, setTrainingTitleOptions] = useState<any>(
     []
   );
  const [selectedDate, setSelectedDate] = useState<any>(
    undefined
  );
  const [selectedDistrict, setSelectedDistrict] = useState<string | undefined>(
    undefined,
  );
  const [selectedBlock, setSelectedBlock] = useState<string | undefined>(
    undefined,
  );
  const [appliedDistrict, setAppliedDistrict] = useState<string | undefined>(
    undefined,
  );

  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // ---- Overall stat strip ----
  const [isLoadingOverallStats, setIsLoadingOverallStats] = useState(false);
  const [overallStats, setOverallStats] = useState<
    any | undefined
  >(undefined);

  const [isLoadingOverallMap, setIsLoadingOverallMap] = useState(false);
  const [overallMap, setOverallMap] = useState<any | undefined>(undefined);

   const [isReportModalOpen, setIsReportModalOpen] = useState(false);
   const [isLoadingReportRows, setIsLoadingReportRows] = useState(false);
   const [reportRows, setReportRows] = useState<ITeacherTrainingReportRow[]>(
     [],
   );


  // ---- Morning / Evening session data (map + table), fetched independently ----
  const [isLoadingMorning, setIsLoadingMorning] = useState(false);
  const [morningData, setMorningData] = useState<
    any | undefined
  >(undefined);

  const [isLoadingEvening, setIsLoadingEvening] = useState(false);
  const [eveningData, setEveningData] = useState<
    any | undefined
  >(undefined);

   const [selectedSession, setSelectedSession] = useState<string | undefined>(
      parseInt(moment().format("MM"), 10) > 4
        ? moment().add(1, "year").format("YYYY")
        : moment().format("YYYY"),
    );

    const [dateRange, setDateRange] = useState<any>({
      min: null,
      max: null,
    });

    const [initialLoadDone, setInitialLoadDone] = useState(false);
  useEffect(() => {
    if (selectedDistrict) getBlocksByDistrictId(+selectedDistrict);
  }, [selectedDistrict]);

  // Initial load, mirrors main Dashboard's "load once on mount" behaviour
 

  const buildPayload = () => ({
    Date: selectedDate
      ? moment(selectedDate).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD"),
    DistrictId: Number(selectedDistrict ?? 0),
    BlockId: Number(selectedBlock ?? 0),
  });

  const getOverallStats = async () => {
    setIsLoadingOverallStats(true);
     const res = await fetchTeacherTrainingData(
       `/GetTrainingScheduleSummary?districtId=${selectedDistrict ?? 0}&blockId=${selectedBlock ?? 0}&scheduleDate=${selectedDate ? moment(selectedDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")}&training_Schedule_Header_Id=${trainingTitle ?? 0}`,
     );
    if (res.isSuccess) {
      setOverallStats(res?.data[0]);
    } else {
      setOverallStats(undefined);
      showToast({
        type: "error",
        message: res?.message || "Failed to load overall stats",
      });
    }
    setIsLoadingOverallStats(false);
  };


  const getOverallMap = async () => {
    setIsLoadingOverallMap(true);
    const res = await fetchTeacherTrainingData(
      `/GetTrainingScheduleMapSummary?districtId=${selectedDistrict ?? 0}&blockId=${selectedBlock ?? 0}&scheduleDate=${selectedDate ? moment(selectedDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")}&training_Schedule_Header_Id=${trainingTitle ?? 0}`,
    );
    if (res.isSuccess) {
      setOverallMap(res?.data);
    } else {
      setOverallMap(undefined);
      showToast({
        type: "error",
        message: res?.message || "Failed to load overall map",
      });
    }
    setIsLoadingOverallMap(false);
  };



  const getSessionData = async (session: "morning" | "evening") => {
    const setLoading =
      session === "morning" ? setIsLoadingMorning : setIsLoadingEvening;
    const setData = session === "morning" ? setMorningData : setEveningData;

    setLoading(true);
    const res = await PostTeacherTrainingData(ENDPOINTS.SESSION_DATA, {
      ...buildPayload(),
      Session: session,
    });
    if (res.isSuccess) {
      setData(res?.data);
    } else {
      setData(undefined);
      showToast({
        type: "error",
        message: res?.message || `Failed to load ${session} attendance`,
      });
    }
    setLoading(false);
  };

    const getSessionOptions = async () => {
      const res = await fetchTeacherTrainingData(
        `/GetTrainingScheduleBySession?current_Session=${selectedSession}`,
      );
      if (res.isSuccess) {
        setTrainingTitleOptions(res?.data);
      } else {
        setTrainingTitleOptions(undefined);
        showToast({
          type: "error",
          message: res?.message || `Failed to load sessions`,
        });
      }
    };

  const handleApplyFilter = () => {
    getOverallStats();
    getOverallMap();
    // getSessionData("morning");
    // getSessionData("evening");
    setLastUpdated(new Date());
    setAppliedDistrict(selectedDistrict ?? undefined);
  };

  const handleClearFilter = () => {
    setSelectedDate(new Date());
    setSelectedDistrict(undefined);
    setSelectedBlock(undefined);
    setTrainingTitle("morning");
    setAppliedDistrict(undefined);
    // Re-fetch with cleared filters, matching the mockup's Clear -> reset behaviour
    setTimeout(handleApplyFilter, 0);

  };

  const handleRefresh = () => handleApplyFilter();

  // ---- Download helpers ----
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  const downloadMap = async (session: "morning" | "evening") => {
    const res = await PostTeacherTrainingData(ENDPOINTS.DOWNLOAD_MAP, {
      ...buildPayload(),
      Session: session,
    });
    if (!res.isSuccess) {
      showToast({ type: "error", message: "Failed to download map" });
    }
    // actual file-save wiring depends on whether your API returns a blob/URL —
    // follow whatever pattern your existing "downloadableData" export uses.
  };

  // Fetches the flat teacher-level rows and generates the styled XLSX
  // client-side, matching the exact "data dump" format you shared.
  const downloadReport = async () => {
    setIsDownloadingReport(true);
    const res = await fetchTeacherTrainingData(
      `/GetTrainingScheduleReport?districtId=${selectedDistrict ?? 0}&blockId=${selectedBlock ?? 0}&scheduleDate=${selectedDate ? moment(selectedDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")}&training_Schedule_Header_Id=${trainingTitle ?? 0}`,
    );
    if (res.isSuccess) {
      const rows: ITeacherTrainingReportRow[] = res?.data ?? [];
      if (rows.length === 0) {
        showToast({
          type: "error",
          message: "No records to export for this filter",
        });
      } else {
        const fileSuffix = moment(selectedDate ?? new Date()).format(
          "DD-MM-YYYY",
        );
       let ATTENDANCE_REPORT_COLUMNS:any = [
         { header: "Training Title", key: "trainingTitle", width: 22 },
         { header: "Training Start Date", key: "start_date", width: 14 },
         { header: "Training End Date", key: "end_date", width: 14 },
         { header: "Date", key: "trainingDate", width: 14 },
         { header: "District", key: "district", width: 16 },
         { header: "Block", key: "block", width: 16 },
         { header: "Training Batch", key: "training_Batch", width: 16 },
         { header: "Name of the teacher", key: "nameoftheteacher", width: 22 },
         { header: "Employee Code", key: "employeeCode", width: 16 },
         {
           header: "Teacher Contact Number",
           key: "teacherMobileNo",
           width: 20,
         },
         { header: "School Name", key: "school_Name", width: 22 },
         { header: "School Code", key: "schoolCode", width: 14 },
         {
           header: "School Cluster Name",
           key: "cluster_School_Name",
           width: 20,
         },
         {
           header: "Cluster UDISE Code",
           key: "cluster_UDISE_Code",
           width: 18,
         },
         { header: "Session", key: "session", width: 12 },
         {
           header: "Status - Present/Absent",
           key: "statusPresentAbsent",
           width: 20,
         },
         { header: "Attendance Time", key: "attendanceTime", width: 16 },
       ];
        generateExcelReport(
          rows,
          ATTENDANCE_REPORT_COLUMNS,
          `TeacherTrainingAttendance_Report_${fileSuffix}`,
          "Training Title",
        );
      }
    } else {
      showToast({
        type: "error",
        message: res?.message || "Failed to download report",
      });
    }
    setIsDownloadingReport(false);
  };


    const handleViewReport = async () => {
      setIsLoadingReportRows(true);
    const res = await fetchTeacherTrainingData(
      `/GetTrainingScheduleReport?districtId=${selectedDistrict ?? 0}&blockId=${selectedBlock ?? 0}&scheduleDate=${selectedDate ? moment(selectedDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")}&training_Schedule_Header_Id=${trainingTitle ?? 0}`,
    );
      if (res.isSuccess) {
        const rows: ITeacherTrainingReportRow[] = res?.data ?? [];
        if (rows.length === 0) {
          showToast({ type: "error", message: "No records for this filter" });
        } else {
          setReportRows(rows);
          setIsReportModalOpen(true);
        }
      } else {
        showToast({
          type: "error",
          message: res?.message || "Failed to load report",
        });
      }
      setIsLoadingReportRows(false);
    };


    useEffect(() => {
      getDistrictByStateId(1);
      getSession(false);
      if (role?.includes(ROLES.BLOCK)) {
        setSelectedDistrict(
          String(user?.details?.blockUserDetails?.district_Id),
        );
        setSelectedBlock(String(user?.details?.blockUserDetails?.block_Id));
      } else if (role?.includes(ROLES.DISTRICT)) {
        setSelectedDistrict(
          String(user?.details?.districtUserDetails?.district_Id),
        );
      }
    }, [])


    useEffect(() => {
      getSessionOptions();
    }, [selectedSession]);

    useEffect(() => {
      if (trainingTitleOptions?.length > 0) {
        const selectedOption = trainingTitleOptions?.find(
          (option: any) => option?.training_Schedule_Header_Id == trainingTitle,
        );
        if (selectedOption) {
          setDateRange({
            min: new Date(selectedOption?.start_Date),
            max: new Date(selectedOption?.end_Date),
          });
        }
      }
    }, [trainingTitle]);


    useEffect(()=>{
if(trainingTitleOptions?.length>0){
  setTrainingTitle(String(trainingTitleOptions[0]?.training_Schedule_Header_Id));
  setSelectedDate(new Date(trainingTitleOptions[0]?.start_Date))
}
    },[trainingTitleOptions])


    useEffect(() => {
      if (sessions?.length>0) {
       const sessionId = sessions?.find(
             (option) =>
               option?.session_Name ==
               calculateDefaultSession(parseInt(moment().format("M")))
           )?.session_Id
         setSelectedSession(String(sessionId));
      }
    }, [sessions]);


    const morningMapData = React.useMemo(() => {
      if (!Array.isArray(overallMap)) return [];
      return overallMap.map((e: any) => ({
        name: e?.block_name ? e?.block_name : e?.district_Name,
        Present: e?.morningPresent ?? 0,
        Percentage: e?.morningPercentage ?? 0,
      }));
    }, [overallMap]);

    const eveningMapData = React.useMemo(() => {
      if (!Array.isArray(overallMap)) return [];
      return overallMap.map((e: any) => ({
        name: e?.block_name ? e?.block_name : e?.district_Name,
        Present: e?.eveningPresent ?? 0,
        Percentage: e?.eveningPercentage ?? 0,
      }));
    }, [overallMap]);

     useEffect(() => {
      if(trainingTitle && selectedDate && !initialLoadDone){
        setInitialLoadDone(true)
         handleApplyFilter();
      }
     }, [trainingTitle, selectedDate,]);

    

  return {
    // filters
    trainingTitle,
    setTrainingTitle,
    selectedDate,
    setSelectedDate,
    selectedDistrict,
    setSelectedDistrict,
    selectedBlock,
    setSelectedBlock,
    districts,
    blocks,
    handleApplyFilter,
    handleClearFilter,
    handleRefresh,
    lastUpdated,

    // overall stats
    isLoadingOverallStats,
    overallStats,

    // sessions
    isLoadingMorning,
    morningData,
    isLoadingEvening,
    eveningData,

    // downloads
    downloadMap,
    downloadReport,
    isDownloadingReport,
    setSelectedSession,
    selectedSession,
    sessions,
    trainingTitleOptions,
    dateRange,
    overallMap,
    morningMapData,
    eveningMapData,
    appliedDistrict,
    isLoadingOverallMap,
    isReportModalOpen,
    setIsReportModalOpen,
    isLoadingReportRows,
    reportRows,
    handleViewReport,
  };
}
